'use client'

import { createElement, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import type { ImageField } from '@prismicio/client'
import { PrismicNextImage } from '@prismicio/next'
import { cn } from '@/utils/twMerge'
import { animatedIconRegistry } from './animated-icons'

type AnimatedIconProps = {
  animatedIcon?: string | null
  icon: ImageField
  className?: string
}

const subscribeNoop = () => () => {}
const getClientHydrated = () => true
const getServerHydrated = () => false

const AnimatedIcon = ({ animatedIcon, icon, className }: AnimatedIconProps) => {
  const IconComponent = animatedIcon ? animatedIconRegistry[animatedIcon] : undefined
  const containerRef = useRef<HTMLDivElement>(null)
  const hasPlayed = useRef(false)
  const [playing, setPlaying] = useState(false)
  const [reduceMotion, setReduceMotion] = useState(false)
  const hydrated = useSyncExternalStore(subscribeNoop, getClientHydrated, getServerHydrated)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReduceMotion(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    if (!IconComponent || reduceMotion || hasPlayed.current) return

    const el = containerRef.current
    if (!el || !('IntersectionObserver' in window)) {
      hasPlayed.current = true
      setPlaying(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !hasPlayed.current) {
          hasPlayed.current = true
          setPlaying(true)
          observer.disconnect()
        }
      },
      { threshold: 0.55, rootMargin: '0px 0px -12% 0px' },
    )
    observer.observe(el)

    return () => observer.disconnect()
  }, [IconComponent, reduceMotion])

  const iconNode = IconComponent ? (
    createElement(IconComponent)
  ) : (
    <PrismicNextImage field={icon} fallbackAlt="" className="h-auto max-h-full w-auto max-w-full object-contain" />
  )

  return (
    <div
      ref={containerRef}
      className={cn(
        'ss-animated-icon flex h-[144px] w-[160px] shrink-0 items-center justify-center overflow-visible',
        className,
      )}
      data-icon={animatedIcon ?? undefined}
      data-hydrated={hydrated ? 'true' : undefined}
      data-playing={playing ? 'true' : undefined}
      data-reduced={reduceMotion ? 'true' : undefined}
      aria-hidden={IconComponent ? 'true' : undefined}>
      {iconNode}
    </div>
  )
}

export default AnimatedIcon
